import { SUPPORTED_EXCHANGE } from '@/lib/configs/exchange-config'
import { tradeVenueToExchangeIconKey } from '@/lib/configs/exchange-registry'
import { WatchListTab } from '@/lib/configs/proto-config'
import { Exchange, InstrumentKind, TradeVenue } from '@/types/generated/common/types'

export function toExchangeEnum(exchange: keyof typeof SUPPORTED_EXCHANGE): Exchange {
  switch (exchange) {
    case SUPPORTED_EXCHANGE.BINANCE:
      return Exchange.EXCHANGE_BINANCE
    case SUPPORTED_EXCHANGE.BYBIT:
      return Exchange.EXCHANGE_BYBIT
    case SUPPORTED_EXCHANGE.HYPERLIQUID:
      return Exchange.EXCHANGE_HYPERLIQUID
    case SUPPORTED_EXCHANGE.DERIBIT:
      return Exchange.EXCHANGE_DERIBIT
    case SUPPORTED_EXCHANGE.DERIVE:
      return Exchange.EXCHANGE_DERIVE
    case SUPPORTED_EXCHANGE.UPBIT:
      return Exchange.EXCHANGE_UPBIT
    case SUPPORTED_EXCHANGE.LIGHTER:
      return Exchange.EXCHANGE_LIGHTER
    case SUPPORTED_EXCHANGE.TOSS:
      return Exchange.EXCHANGE_XKRX
    case SUPPORTED_EXCHANGE.PAPERTRADE:
      return Exchange.EXCHANGE_PAPERTRADE
    case SUPPORTED_EXCHANGE.GLBX:
      return Exchange.EXCHANGE_GLBX
    case SUPPORTED_EXCHANGE.XNAS:
      return Exchange.EXCHANGE_XNAS
    case SUPPORTED_EXCHANGE.XNYS:
      return Exchange.EXCHANGE_XNYS
    case SUPPORTED_EXCHANGE.OPRA:
      return Exchange.EXCHANGE_OPRA
    default:
      // Unmapped/unrecognized venue must not silently masquerade as Deribit data.
      return Exchange.EXCHANGE_UNSPECIFIED
  }
}

export function toExchangeString(exchange: Exchange): keyof typeof SUPPORTED_EXCHANGE {
  switch (exchange) {
    case Exchange.EXCHANGE_BINANCE:
      return SUPPORTED_EXCHANGE.BINANCE
    case Exchange.EXCHANGE_BYBIT:
      return SUPPORTED_EXCHANGE.BYBIT
    case Exchange.EXCHANGE_HYPERLIQUID:
      return SUPPORTED_EXCHANGE.HYPERLIQUID
    case Exchange.EXCHANGE_DERIVE:
      return SUPPORTED_EXCHANGE.DERIVE
    case Exchange.EXCHANGE_UPBIT:
      return SUPPORTED_EXCHANGE.UPBIT
    case Exchange.EXCHANGE_LIGHTER:
      return SUPPORTED_EXCHANGE.LIGHTER
    case Exchange.EXCHANGE_XKRX:
      return SUPPORTED_EXCHANGE.TOSS
    case Exchange.EXCHANGE_PAPERTRADE:
      return SUPPORTED_EXCHANGE.PAPERTRADE
    case Exchange.EXCHANGE_GLBX:
      return SUPPORTED_EXCHANGE.GLBX
    case Exchange.EXCHANGE_XNAS:
      return SUPPORTED_EXCHANGE.XNAS
    case Exchange.EXCHANGE_XNYS:
      return SUPPORTED_EXCHANGE.XNYS
    case Exchange.EXCHANGE_OPRA:
      return SUPPORTED_EXCHANGE.OPRA
    case Exchange.EXCHANGE_DERIBIT:
    default:
      return SUPPORTED_EXCHANGE.DERIBIT
  }
}

/**
 * Parses a `SelectedInstrument.exchange`/`UnifiedItemValues.exchange`-style field —
 * a stringified numeric `Exchange` enum (e.g. `"12"` for Upbit, written via
 * `someExchangeEnum.toString()`), NOT a `SUPPORTED_EXCHANGE` key like `'UPBIT'`
 * (that's what `toExchangeString`/`toExchangeEnum` deal in). Guards against
 * `NaN` from an empty/undefined/malformed value, which would otherwise leak
 * into a React Query key (e.g. `useInstrumentList`).
 */
export function parseExchangeField(value: string | undefined, fallback: Exchange): Exchange {
  const n = value ? Number(value) : NaN
  return Number.isFinite(n) ? (n as Exchange) : fallback
}

export function tradeVenueToIconKey(venue: TradeVenue): string {
  return tradeVenueToExchangeIconKey(venue)
}

export function tradeVenueToExchangeKey(venue: TradeVenue): keyof typeof SUPPORTED_EXCHANGE {
  switch (venue) {
    case TradeVenue.TRADE_VENUE_BINANCE:
      return SUPPORTED_EXCHANGE.BINANCE
    case TradeVenue.TRADE_VENUE_BYBIT:
      return SUPPORTED_EXCHANGE.BYBIT
    case TradeVenue.TRADE_VENUE_HYPERLIQUID:
      return SUPPORTED_EXCHANGE.HYPERLIQUID
    case TradeVenue.TRADE_VENUE_DERIVE:
      return SUPPORTED_EXCHANGE.DERIVE
    case TradeVenue.TRADE_VENUE_UPBIT:
      return SUPPORTED_EXCHANGE.UPBIT
    case TradeVenue.TRADE_VENUE_LIGHTER:
      return SUPPORTED_EXCHANGE.LIGHTER
    case TradeVenue.TRADE_VENUE_TOSS:
      return SUPPORTED_EXCHANGE.TOSS
    case TradeVenue.TRADE_VENUE_PAPERTRADE:
      return SUPPORTED_EXCHANGE.PAPERTRADE
    case TradeVenue.TRADE_VENUE_DERIBIT:
    default:
      return SUPPORTED_EXCHANGE.DERIBIT
  }
}

export function toInstrumentKindEnum(tab: WatchListTab): InstrumentKind {
  switch (tab) {
    case WatchListTab.SPOT:
      return InstrumentKind.INSTRUMENT_KIND_SPOT
    case WatchListTab.FUTURE:
      return InstrumentKind.INSTRUMENT_KIND_FUTURE
    case WatchListTab.PERPS:
      return InstrumentKind.INSTRUMENT_KIND_FUTURE
    case WatchListTab.OPTIONS:
      return InstrumentKind.INSTRUMENT_KIND_OPTION
    default:
      return InstrumentKind.INSTRUMENT_KIND_UNSPECIFIED
  }
}
